// PostHog event helpers for the Search surface.
//
// Defined once so every call site uses the same event names and payload
// shape — PostHog filters break the moment the same logical event is
// captured under two different names ("search_run" vs "search_executed"
// vs "ran_search"). All event names are namespaced ``search_*`` so they
// roll up cleanly in the PostHog dashboard.
//
// Callers pass the PostHog client they already pulled via
// ``usePostHog()`` — we deliberately don't import the hook here so this
// module stays renderless-and-testable.

import type posthog from 'posthog-js';

type PH = typeof posthog | null | undefined;

/** Tab the event happened on. Mirrors the SearchTab union from
 *  search-shell — keep them in sync. People uses /people-search/*
 *  endpoints, Companies hits /companies/search. */
export type SearchTab = 'people' | 'companies' | 'company';

/** A new search dispatch (cache-miss path or explicit user click).
 *  Cache-hit returns deliberately don't fire — they aren't a user
 *  "search" in any meaningful sense and would skew the volume metric. */
export function trackSearchExecuted(
  ph: PH,
  payload: {
    tab: SearchTab;
    /** Total number of populated filter slots (rough complexity proxy). */
    filterCount: number;
    /** True iff the dispatch was routed through the composite
     *  /people-search/by-companies endpoint instead of the plain
     *  /search. Lets us track composite adoption vs. the slower path. */
    composite: boolean;
    /** Page-size limit the user asked for. */
    limit?: number;
  }
): void {
  ph?.capture('search_executed', payload);
}

/** A search dispatch came back with a response (a network success, not a
 *  cache hit or a failure): the pair of ``search_executed``, which fires before
 *  the request and cannot carry a count. */
export function trackSearchCompleted(
  ph: PH,
  payload: {
    tab: SearchTab;
    /** The total the search reported, or the rows it returned when it gave
     *  none. Not the number of rows on screen. */
    total: number;
    /** Same meaning as on ``search_executed``: the composite endpoint ran. */
    composite: boolean;
    /** The provider reported a total; false when ``total`` counts the rows. */
    totalKnown: boolean;
    /** The total is before Cleanlist or CRM exclusions, so at most that. */
    totalIsUpperBound: boolean;
  }
): void {
  ph?.capture('search_completed', payload);
}

/** A People "Add to list" import finished (every chunk POST resolved).
 *  ``durationMs`` covers ONLY the chunked import phase — the part the
 *  bounded-parallel worker pool optimized — NOT the upstream re-fetch that
 *  precedes it. Lets us chart p50/p95 import latency and compare the parallel
 *  path against the old ~30–40s sequential baseline. Fires once per import
 *  attempt, on success or partial failure (``failedChunks`` carries the
 *  outcome), so it doubles as an import-volume metric. */
export function trackPeopleImportCompleted(
  ph: PH,
  payload: {
    tab: SearchTab;
    /** Rows actually POSTed, after client-side LinkedIn-identity dedupe. */
    requestedCount: number;
    /** Backend-authoritative rows newly added to the list. */
    importedCount: number;
    /** Backend-authoritative rows skipped as duplicates. */
    skippedCount: number;
    /** How many 500-row chunks the batch was split into. */
    chunkCount: number;
    /** Chunks whose POST failed (partial-success signal; >0 means re-run). */
    failedChunks: number;
    /** Max chunks in flight at once — the parallelism degree. */
    maxConcurrency: number;
    /** Enrichment mode requested with the import (none/email/email_phone). */
    enrichment: string;
    /** Wall-clock of the chunked import phase, ms. */
    durationMs: number;
  }
): void {
  ph?.capture('people_import_completed', payload);
}

/** A user removed one filter via the active-chip strip or sidebar X. */
export function trackSearchFilterRemoved(
  ph: PH,
  payload: { tab: SearchTab; filterKey: string }
): void {
  ph?.capture('search_filter_removed', payload);
}

/** The router decided to send the dispatch through the composite
 *  endpoint. Useful for tracking how often firmographic-heavy queries
 *  occur — the composite path is slower (CompanyDB roundtrip then
 *  PersonDB scoped) so volume here drives prioritization. */
export function trackSearchCompositeRouted(
  ph: PH,
  payload: { tab: SearchTab; reason: string; companyFilterKeys: string[] }
): void {
  ph?.capture('search_composite_routed', payload);
}

/** The user clicked the "Find contacts" CTA on selected companies. */
export function trackSearchBulkFindContactsClicked(
  ph: PH,
  payload: {
    companyCount: number;
    /** Whether the click landed on the merge/replace confirm dialog
     *  (meaning the user already had People filters set) or applied
     *  filters directly. */
    requiredConfirm: boolean;
    /** Where the click originated. ``search-table`` is the in-page
     *  Companies-tab toolbar; ``companies-sheet`` is the lead-list
     *  Companies sheet Actions drawer reroute. */
    source?: 'search-table' | 'companies-sheet';
  }
): void {
  ph?.capture('search_bulk_find_contacts_clicked', payload);
}

/** A user resolved the merge/replace confirm dialog. ``mode`` is the
 *  choice they made. */
export function trackSearchBulkFindContactsResolved(
  ph: PH,
  payload: { mode: 'merge' | 'replace' | 'cancel'; companyCount: number }
): void {
  ph?.capture('search_bulk_find_contacts_resolved', payload);
}

/** A search dispatch failed and the error banner is now visible. */
export function trackSearchErrorShown(
  ph: PH,
  payload: { tab: SearchTab; message: string; status?: number }
): void {
  ph?.capture('search_error_shown', payload);
}
